import React, {useCallback} from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import TableFooter from "@material-ui/core/TableFooter";
import {TablePaginationActions} from "../table-pagination-actions/TablePaginationActions";
import TableLoader from 'components/ui/tables/rows/table-loader/TableLoader';
import TableWithCheckboxesHead from "../rows/custom-table-header/CustomTableHeader";
import {useTableWithCheckboxesStyles} from "./TableWithCheckboxesStyles";
import PropTypes from "prop-types";
import SelectAllRow from "../rows/select-all-row/SelectAllRow";

const TableWithCheckboxes = (props) => {
    const classes = useTableWithCheckboxesStyles();

    /**
     * Returns list of rows on current page that are selected/not selected.
     * @param {boolean} toSelect Parameter that decides if the function should return list of rows on page
     * that are selected or the ones that are not selected
     * @return {array} The selected/not selected rows on the current page.
     */
    const getRowsOnCurrentPage = useCallback((toSelect = true) => {
        if (!props.isClientSide) {
            return props.rows.filter(row => toSelect ?
                props.selected.findIndex(i => row.id === i.id) === -1 :
                props.selected.findIndex(i => row.id === i.id) !== -1);
        }
        return props.rows
            .slice(props.page * props.rowsPerPage, props.page * props.rowsPerPage + props.rowsPerPage)
            .filter(row => toSelect ?
                props.selected.findIndex(i => row.id === i.id) === -1 :
                props.selected.findIndex(i => row.id === i.id) !== -1);
    }, [props.isClientSide, props.rows, props.page, props.rowsPerPage, props.selected])

    const isVisibleSelectAllRow = !props.selectAllRows && props.selectAllRows !== undefined &&
        props.count > props.rowsPerPage && props.selected.length !== props.count &&
        ((getRowsOnCurrentPage(false).length === props.rows.length) ||
            (props.isClientSide && getRowsOnCurrentPage(false).length === props.rows
                .slice(props.page * props.rowsPerPage, props.page * props.rowsPerPage + props.rowsPerPage).length))

    const handleRemoveSelectAllServerSide = () => {
        if (!props.isClientSide && props.selectAllRows !== undefined) {
            props.setSelectAllRows(false)
        }
    }

    const getRowCount = props.isClientSide && !props.selectAllRows ?
        props.rows.slice(props.page * props.rowsPerPage, props.page * props.rowsPerPage + props.rowsPerPage).length :
        props.rows.length

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            if (props.isClientSide && props.selectAllRows) {
                props.setSelected(props.rows);
            } else {
                let newSelected = getRowsOnCurrentPage();
                let tmp = [...props.selected].concat(newSelected);
                props.setSelected(tmp);
            }
            return;
        }
        if (props.selectAllRows !== undefined) {
            props.setSelectAllRows(false)
        }
        props.setSelected(deselectRowsOnPage());
    };

    const deselectRowsOnPage = () => {
        if (props.isClientSide && !props.selectAllRows) {
            return props.selected
                .filter(row => props.rows.slice(props.page * props.rowsPerPage, props.page * props.rowsPerPage + props.rowsPerPage)
                    .findIndex(i => row.id === i.id) === -1);
        }
        return props.selected.filter(row => props.rows.findIndex(i => row.id === i.id) === -1);
    }

    const handleChangePage = (event, newPage) => {
        handleRemoveSelectAllServerSide()
        props.fetchData(newPage, props.rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        handleRemoveSelectAllServerSide()
        props.fetchData(0, parseInt(event.target.value, 10));
    }

    const isSelectAllFull = () => {
        if (props.isClientSide) {
            if (props.selectAllRows) {
                return props.selected.length
            }
            return getRowsOnCurrentPage(false).length
        }
        return props.rows.length - getRowsOnCurrentPage()?.length;
    }

    const handleSelectAllRowButton = () => {
        props.setSelected(props.rows);
        if (props.selectAllRows !== undefined) {
            props.setSelectAllRows(true)
        }
    }

    return (
        <Paper className={classes.paper}>
            <TableContainer>
                <Table
                    className={classes.table}
                    aria-labelledby="tableTitle"
                    size={'medium'}
                    aria-label="table with checkboxes"
                >
                    <TableWithCheckboxesHead
                        numSelected={isSelectAllFull()}
                        onSelectAllClick={handleSelectAllClick}
                        canSelectAll={props.canSelectAll}
                        rowCount={getRowCount}
                        headCells={props.headCells}
                    />
                    <TableBody>
                        {!props.loading ? <>
                                {isVisibleSelectAllRow ? <SelectAllRow colSpan={props.headCells.length + 2}
                                                                       handleSelectAllButton={handleSelectAllRowButton}
                                                                       totalRows={props.count}/> : null}
                                {props.tableRows}
                            </> :
                            <TableLoader loading={props.loading} colSpan={props.headCells.length + 2}/>}
                    </TableBody>
                    {props.hasPagination ? <TableFooter>
                        <tr>
                            {props.count !== 0 && !props.loading ? <TablePagination
                                rowsPerPageOptions={[5, 15, 25]}
                                count={props.count}
                                rowsPerPage={props.rowsPerPage}
                                page={props.page}
                                SelectProps={{
                                    inputProps: {'aria-label': 'rows per page'},
                                    native: true,
                                }}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                onPageChange={handleChangePage}/> : null}
                        </tr>
                    </TableFooter> : null}
                </Table>
            </TableContainer>
        </Paper>
    );
}

TableWithCheckboxes.propTypes = {
    page: PropTypes.number,
    count: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number,
    loading: PropTypes.bool.isRequired,
    tableRows: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    headCells: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
    selected: PropTypes.array.isRequired,
    setSelected: PropTypes.func.isRequired,
    fetchData: PropTypes.func,
    canSelectAll: PropTypes.bool,
    isClientSide: PropTypes.bool,
    hasPagination: PropTypes.bool,
    selectAllRows: PropTypes.bool,
    setSelectAllRows: PropTypes.func
}

TableWithCheckboxes.defaultProps = {
    canSelectAll: false,
    isClientSide: false,
    hasPagination: true
};

export default TableWithCheckboxes;
